import { createPortal } from 'react-dom';
import { useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';
import { toast } from 'sonner';
import { X, ScanLine } from 'lucide-react';
import { parseShareCode, fetchShared, applyShared } from '../lib/catalogShare';
import { fill } from '../lib/license';

function useQrScanner(videoRef, active, onText) {
  const [camErr, setCamErr] = useState(false);
  useEffect(() => {
    if (!active) return undefined;
    let stream, timer, stop = false;
    const det = 'BarcodeDetector' in window ? new window.BarcodeDetector({ formats: ['qr_code'] }) : null;
    const canvas = document.createElement('canvas');
    const tick = async () => {
      const v = videoRef.current;
      if (stop || !v) return;
      if (v.readyState >= 2) {
        let txt = null;
        if (det) txt = (await det.detect(v).catch(() => []))[0]?.rawValue;
        else {
          canvas.width = v.videoWidth; canvas.height = v.videoHeight;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(v, 0, 0);
          txt = jsQR(ctx.getImageData(0, 0, canvas.width, canvas.height).data, canvas.width, canvas.height)?.data;
        }
        if (txt && onText(txt)) return;
      }
      timer = setTimeout(tick, 250);
    };
    navigator.mediaDevices?.getUserMedia({ video: { facingMode: 'environment' } })
      .then((s) => { stream = s; if (stop) return s.getTracks().forEach((x) => x.stop()); videoRef.current.srcObject = s; videoRef.current.play().catch(() => {}); tick(); })
      .catch(() => setCamErr(true));
    return () => { stop = true; clearTimeout(timer); stream?.getTracks().forEach((x) => x.stop()); };
  }, [active, videoRef, onText]);
  return camErr;
}

export default function CatalogScan({ t, onClose }) {
  const videoRef = useRef(null);
  const [manual, setManual] = useState('');
  const [found, setFound] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async (code) => {
    setBusy(true);
    try { setFound(await fetchShared(code)); } catch (e) { toast.error(e.message === 'notfound' ? t.catScanNotFound : t.catShareErr); }
    setBusy(false);
  };
  const onText = useRef((txt) => { const c = parseShareCode(txt); if (c) load(c); return !!c; }).current;
  const camErr = useQrScanner(videoRef, !found, onText);
  const apply = (merge) => { applyShared(found, merge); toast.success(t.catImported); onClose(); };
  return createPortal(
    <div className="editor-backdrop" data-testid="catalog-scan-dialog">
      <div className="editor cat-share">
        <div className="editor-head">
          <h4><ScanLine size={18} /> {t.catScanTitle}</h4>
          <button className="icon-btn" data-testid="catalog-scan-close-btn" onClick={onClose}><X size={16} /></button>
        </div>
        {found ? (
          <div className="scan-found" data-testid="catalog-scan-found">
            <b>{fill(t.catScanFound, { n: found.items.length })}</b>
            <div className="row">
              <button className="btn btn-dark btn-sm" data-testid="catalog-scan-replace-btn" onClick={() => window.confirm(t.catScanReplaceConfirm) && apply(false)}>{t.catScanReplace}</button>
              <button className="btn btn-green btn-sm" data-testid="catalog-scan-merge-btn" onClick={() => apply(true)}>{t.catScanMerge}</button>
            </div>
            <p className="muted small-hint">{t.catScanMergeHint}</p>
          </div>
        ) : (
          <>
            {camErr ? <div className="empty" data-testid="catalog-scan-cam-error">{t.catScanCamErr}</div> : (
              <div className="scan-view"><video ref={videoRef} muted playsInline data-testid="catalog-scan-video" /><i className="scan-frame" /></div>
            )}
            <p className="muted small-hint">{t.catScanHint}</p>
            <div className="row">
              <input data-testid="catalog-scan-code-input" maxLength={14} placeholder={t.catScanManual} value={manual} onChange={(e) => setManual(e.target.value.toUpperCase())} />
              <button className="btn btn-dark btn-sm" data-testid="catalog-scan-load-btn" disabled={busy || !parseShareCode(manual)} onClick={() => load(parseShareCode(manual))}>{t.catScanLoad}</button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
